import * as React from "react";
import { TitleableRoute } from "./TitleableRoute";
import { useDispatch, useSelector } from "react-redux";

import { ApplicationState } from "../store";
import { getUserContext } from '../store/reducers/UserContext';
import { services } from '../services';
import { useBackgroundPromiseEffectByCondition } from '../helpers/hooks/useBackgroundPromiseEffectByCondition';
import { Navigate, useLocation } from "react-router";
import { useKeycloak } from "../contexts/KeycloakContext";
import styles from "./PrivateRoute.module.css";

interface PrivateRouteProps {
    title: string;
    component: React.ComponentType<any>;
    path: string;
    allowWithoutContractors?: boolean;
}

const AuthLoading: React.FC<{ subtitle: string }> = ({ subtitle }) => (
  <div className={styles.page}>
    <div className={styles.gridOverlay} />

    <div className={styles.logoArea}>
      <img src="/logo.png" alt="X5 Real Estate" className={styles.logo} />
    </div>

    <div className={styles.stage}>
      <div className={styles.animStage}>
        <div className={`${styles.ripple} ${styles.rip1}`} />
        <div className={`${styles.ripple} ${styles.rip2}`} />
        <div className={`${styles.ripple} ${styles.rip3}`} />

        <div className={styles.track} />
        <div className={styles.conicRing} />
        <div className={styles.dashedRing} />

        <div className={styles.orb1}><span className={styles.dot1} /></div>
        <div className={styles.orb2}><span className={styles.dot2} /></div>
        <div className={styles.orb3}><span className={styles.dot3} /></div>

        <div className={styles.core}>
          <i className={`fa-solid fa-shield-halved ${styles.glyph}`} />
        </div>
      </div>

      <div className={styles.wordmarkArea}>
        <div className={styles.wordmark}>Авторизация</div>
        <div className={styles.subtitle}>{subtitle}</div>
      </div>

      <div className={styles.dots}>
        <span className={`${styles.dotPulse} ${styles.d1}`} />
        <span className={`${styles.dotPulse} ${styles.d2}`} />
        <span className={`${styles.dotPulse} ${styles.d3}`} />
      </div>
    </div>

    <div className={styles.footer}>Защищённое соединение · X5 CPDA</div>
  </div>
);

export const PrivateRoute: React.FC<PrivateRouteProps> = ({ title, component: Component, path, allowWithoutContractors }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { initialized, authenticated } = useKeycloak();

  const userContext = useSelector((appState: ApplicationState) => appState.context.userContext);
  const profileLoaded = userContext.Login;
  const hasContractors = userContext.Contractors?.length > 0;

  useBackgroundPromiseEffectByCondition(
    () => authenticated && !profileLoaded,
    () => services.userService.getCurrentUserContext(),
    (res) => dispatch(getUserContext(res)),
    [authenticated, profileLoaded]
  );

  const renderElement = () => {
    if (!initialized) {
      return <AuthLoading subtitle="Проверяем вашу сессию…" />;
    }

    if (!authenticated) {
      return <Navigate to="/login" state={{ from: location }} replace />;
    }

    if (!profileLoaded) {
      return <AuthLoading subtitle="Загружаем данные профиля…" />;
    }

    if (!allowWithoutContractors && !hasContractors) {
      return <Navigate to="/profile" state={{ from: location }} replace />;
    }

    return <Component />;
  };

  return (
    <TitleableRoute
      title={title}
      element={renderElement()}
    />
  );
};
